import { http, endpoints, logger } from '@/config';
import { DEFAULT_STORE_CODE, REVALIDATE_TIME } from '@/constants';

const ENABLED_PRODUCTS_QUERY =
  '&searchCriteria[filterGroups][2][filters][0][field]=status&searchCriteria[filterGroups][2][filters][0][value]=1&searchCriteria[filterGroups][2][filters][0][condition_type]=eq';

const getCategoryByUrlKey = async ({
  categoryUrlKey,
  store_code,
  categoryId,
}: {
  categoryUrlKey?: string;
  store_code: string;
  categoryId?: string;
}) => {
  if (!categoryUrlKey && !categoryId) {
    return {};
  }
  try {
    const { data } = await http.get(
      `${endpoints.GET_CATEGORIES}/list?searchCriteria[filterGroups][0][filters][0][field]=${
        categoryId ? 'entity_id' : 'url_key'
      }&searchCriteria[filterGroups][0][filters][0][value]=${
        categoryId ? categoryId : categoryUrlKey
      }&searchCriteria[filterGroups][0][filters][0][condition_type]=eq`,
      {
        next: { revalidate: REVALIDATE_TIME },
        store_code,
      }
    );
    return data?.items[0] || {};
  } catch (error) {
    logger.log('Error:', error);
  }
};

const getProducts = async (
  slug: any,
  category: string,
  rtci: string,
  page: number,
  pageSize: number,
  sku?: string | string[] | undefined,
  store_code?: string
) => {
  const categoryid = category;
  const slugid = slug ? slug[slug.length - 1] : '';
  let string = ``;
  let endpoint = ``;

  if (rtci) {
    string += `&searchCriteria[filterGroups][1][filters][0][field]=rtci&searchCriteria[filterGroups][1][filters][0][value]=${rtci}&searchCriteria[filterGroups][1][filters][0][condition_type]=in`;
  }
  if (slugid) {
    endpoint = `?searchCriteria[filterGroups][0][filters][0][field]=category_id&searchCriteria[filterGroups][0][filters][0][value]=${
      categoryid || slugid
    }&searchCriteria[filterGroups][0][filters][0][condition_type]=in${string}&searchCriteria[pageSize]=${
      pageSize || 10
    }&searchCriteria[currentPage]=${page || 1}${ENABLED_PRODUCTS_QUERY}`;
  } else if (categoryid) {
    endpoint = `?searchCriteria[filterGroups][0][filters][0][field]=category_id&searchCriteria[filterGroups][0][filters][0][value]=${categoryid}&searchCriteria[pageSize]=6&searchCriteria[currentPage]=1${ENABLED_PRODUCTS_QUERY}`;
  } else if (sku) {
    endpoint = `?searchCriteria[filterGroups][0][filters][0][field]=sku&searchCriteria[filterGroups][0][filters][0][value]=${sku}&searchCriteria[filterGroups][0][filters][0][condition_type]=in&searchCriteria[pageSize]=${pageSize}&searchCriteria[currentPage]=1${ENABLED_PRODUCTS_QUERY}`;
  } else {
    endpoint = `?searchCriteria[filterGroups][0][filters][0][field]=category_id&searchCriteria[filterGroups][0][filters][0][value]=3&searchCriteria[pageSize]=${
      pageSize || 6
    }&searchCriteria[currentPage]=1${ENABLED_PRODUCTS_QUERY}`;
  }
  try {
    const { data } = await http.get(endpoints.PRODUCT + endpoint, {
      next: { revalidate: REVALIDATE_TIME },
      store_code,
    });
    return data || {};
  } catch (error) {
    logger.log('Error:', error);
  }
};

const getProductsByCategoryId = async ({
  categoryId,
  pageSize,
  store_code,
  noCache = false,
}: {
  categoryId: string | undefined;
  pageSize?: number;
  store_code?: string;
  noCache?: boolean;
}) => {
  const endpoint = `?searchCriteria[filterGroups][0][filters][0][field]=category_id&
    searchCriteria[filterGroups][0][filters][0][value]=${categoryId}&
    searchCriteria[filterGroups][0][filters][0][condition_type]=in&
    searchCriteria[pageSize]=${pageSize || 10}${ENABLED_PRODUCTS_QUERY}`;
  try {
    const { data } = await http.get(`${endpoints.PRODUCT}${endpoint}`, {
      next: { revalidate: noCache ? 0 : REVALIDATE_TIME },
      store_code,
    });
    return data || {};
  } catch (error) {
    logger.log('Error:', error);
  }
};

const getChildCategories = async ({
  slug,
  categoryId,
  store_code = DEFAULT_STORE_CODE,
}: {
  slug?: string;
  categoryId?: number;
  store_code?: string;
}) => {
  const page = slug ?? '3';
  let endpoint = ``;
  if (slug) {
    endpoint = `list?searchCriteria[filterGroups][0][filters][0][field]=parent_id&searchCriteria[filterGroups][0][filters][0][value]=${page}&searchCriteria[filterGroups][0][filters][0][condition_type]=eq&searchCriteria[sortOrders][0][field]=position&searchCriteria[sortOrders][0][direction]=ASC`;
  }
  if (categoryId) {
    endpoint = `${categoryId}`;
  }
  try {
    const { data } = await http.get(`${endpoints.GET_CATEGORIES}/${endpoint}`, {
      store_code,
    });
    const filteredCategories = data?.items.filter(
      (category: any) => category?.is_active
    );
    data.items = filteredCategories;
    return data || {};
  } catch (error) {
    logger.log('Error:', error);
  }
};

const getCategoryById = async ({
  categoryId,
  store_code,
}: {
  categoryId?: string;
  store_code?: string;
}) => {
  if (!categoryId) {
    return {};
  }
  try {
    const { data } = await http.get(
      `${endpoints.GET_CATEGORIES}/${categoryId}`,
      {
        next: { revalidate: REVALIDATE_TIME },
        store_code: store_code || DEFAULT_STORE_CODE,
      }
    );
    return data || {};
  } catch (error) {
    logger.log('Error:', error);
  }
};

const getAttributes = async (name: string, value: boolean) => {
  const string = value ? `/${name}/options` : `/${name}`;
  try {
    const { data } = await http.get(
      `${endpoints.PRODUCT_ATTRIBUTES}${string}`,
      {
        next: { revalidate: REVALIDATE_TIME },
      }
    );
    return data;
  } catch (error) {
    logger.log('error', error);
  }
};

const getGiftingProducts = async (store_code?: string) => {
  try {
    const { data, response } = await http.get(
      `${endpoints.PRODUCT}?searchCriteria[filterGroups][1][filters][0][field]=category_id&searchCriteria[filterGroups][1][filters][0][value]=${process.env.GIFTING_PRODUCTS_CATEGORY_ID}&searchCriteria[filterGroups][1][filters][0][condition_type]=in${ENABLED_PRODUCTS_QUERY}`,
      {
        store_code: store_code || DEFAULT_STORE_CODE,
        next: { revalidate: REVALIDATE_TIME },
      }
    );

    if (response.ok && data?.items) {
      return data.items;
    }
  } catch (error) {
    logger.log('error', error);
  }
};

const getEvents = async ({
  categoryId,
  pageSize,
  websiteId,
}: {
  categoryId: string | undefined;
  pageSize?: number;
  websiteId?: number;
}) => {
  const endpoint = `?searchCriteria[filterGroups][0][filters][0][field]=category_id&
    searchCriteria[filterGroups][0][filters][0][value]=${categoryId}${
      websiteId &&
      `&
    searchCriteria[filterGroups][1][filters][0][field]=website_id&
    searchCriteria[filterGroups][1][filters][0][value]=${websiteId}`
    }&
    searchCriteria[filterGroups][0][filters][0][condition_type]=in&
    searchCriteria[pageSize]=${pageSize || 10}${ENABLED_PRODUCTS_QUERY}`;
  try {
    const { data } = await http.get(`${endpoints.PRODUCT}${endpoint}`, {
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        Pragma: 'no-cache',
        Expires: '0',
      },
      next: { revalidate: 0 },
    });
    return data || {};
  } catch (error) {
    logger.log('Error:', error);
  }
};

export {
  getProducts,
  getChildCategories,
  getAttributes,
  getGiftingProducts,
  getProductsByCategoryId,
  getCategoryById,
  getCategoryByUrlKey,
  getEvents,
};
